<template>
  <div class="bg-white rounded-2xl shadow-sm">
    <div class="pt-3 px-3 sm:px-4 md:px-4 pb-0">
      <h2 class="text-xl sm:text-2xl font-bold">Estadísticas</h2>
    </div>
    <div class="px-3 sm:px-4 md:px-4 pb-4 pt-2">
      <div class="grid grid-cols-1 lg:grid-cols-12 gap-3">
        <div class="lg:col-span-5">
          <PointsEvolutionGraph compact />
        </div>
        <div class="lg:col-span-7 flex flex-col gap-3">
          <div class="grid grid-cols-1 xl:grid-cols-2 gap-3">
            <LevelStats :levelStats="generalStats" />
            <div
              class="flex flex-col justify-between rounded-lg bg-white border border-neutral-200 p-3 pb-3 shadow-sm md:min-w-0"
            >
              <div class="flex items-center justify-between">
                <h3 class="text-xs font-semibold text-neutral-600">Total de ejercicios</h3>
                <span class="text-xl font-bold text-neutral-800">{{ totalExercises }}</span>
              </div>
              <div class="mt-2 grid grid-cols-3 gap-1.5 text-xs font-semibold">
                <div class="rounded-md bg-emerald-50 text-emerald-600 px-1.5 py-0.5 text-center">
                  Fácil<br />
                  <span class="text-sm text-neutral-800">{{ difficultyCounts.easy }}</span>
                </div>
                <div class="rounded-md bg-amber-50 text-amber-600 px-1.5 py-0.5 text-center">
                  Medio<br />
                  <span class="text-sm text-neutral-800">{{ difficultyCounts.medium }}</span>
                </div>
                <div class="rounded-md bg-rose-50 text-rose-600 px-1.5 py-0.5 text-center">
                  Alto<br />
                  <span class="text-sm text-neutral-800">{{ difficultyCounts.hard }}</span>
                </div>
              </div>
            </div>
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div class="rounded-lg border border-neutral-200 bg-white p-3 shadow-sm">
              <p class="text-xs font-semibold text-neutral-600">Total preguntas</p>
              <p class="mt-1 text-2xl font-bold text-indigo-600">{{ totalQuestions }}</p>
            </div>
            <div class="rounded-lg border border-neutral-200 bg-white p-3 shadow-sm">
              <p class="text-xs font-semibold text-neutral-600">Porcentaje de éxito</p>
              <p class="mt-1 text-2xl font-bold text-emerald-600">{{ successRate }}%</p>
            </div>
            <router-link
              to="/statistics"
              class="rounded-lg border border-neutral-200 p-3 shadow-sm flex items-center justify-center gap-2 bg-black hover:bg-neutral-800 text-white font-semibold text-center transition-all text-sm min-h-[72px]"
            >
              <Icon icon="ph:trend-up" height="16" class="text-white shrink-0" />
              <span>Ver Reporte completo de tu desempeño</span>
            </router-link>
            <router-link
              :to="olympicCoursePath"
              class="mob-btn mob-cta-gold rounded-lg p-3 flex items-center justify-center gap-2 text-center text-sm min-h-[72px]"
            >
              <Icon icon="ph:trend-up" height="16" class="text-white shrink-0" />
              <span>Olimpiadas Mobius 2026</span>
            </router-link>
          </div>
          <p
            v-if="isRegisteredInOlimpiadas"
            class="mt-3 text-black font-bold text-base"
          >
            Estás inscrito. Entra a Olimpiadas Mobius 2026 arriba o en la tarjeta de Mis Cursos para ver tus fases.
          </p>
        </div>
      </div>
    </div>
    
    <!-- Categories on the right - COMMENTED OUT FOR NOW -->
      <!--
      <div class="bg-white rounded-xl shadow-sm">
        <div class="p-3 sm:p-4 md:p-5">
          <h3 class="text-xl font-bold text-gray-800 mb-4">Categorías</h3>
        <div
          class="flex gap-2 w-full overflow-x-auto stats-cards"
          v-if="statsChunked.length > 0"
        >
          <swiper
            slidesPerView="auto"
            mousewheel
            freeMode
            navigation
            :pagination="{ clickable: true }"
            :space-between="10"
            :grabCursor="true"
          >
            <swiper-slide
              v-for="(stats, index) in statsChunked"
              :key="index"
              class="flex flex-col gap-2"
            >
              <StudentStatCard
                v-for="(stat, index) in stats"
                :key="index"
                :heading="stat.category"
                :stats="stat.stats"
              />
            </swiper-slide>
          </swiper>
        </div>

        <MobEmptyState
          v-else
          text="No haz hecho ningún curso hasta ahora"
          image="/images/empty-states/student-stats.png"
          />
        </div>
      </div>
      -->
  </div>
</template>

<script>
import { MobEmptyState } from "@/views/V2/components";
import { getEnrolledOlympics } from "@/services/olympics.service";
import { Icon } from "@iconify/vue2";
import LevelStats from "./LevelStats.vue";
import StudentStatCard from "./StudentStatCard.vue";
import PointsEvolutionGraph from "./PointsEvolutionGraph.vue";
import { Mousewheel, Navigation, Pagination } from "swiper";
import { SwiperCore, Swiper, SwiperSlide } from "swiper-vue2";
import "swiper/swiper-bundle.css";

SwiperCore.use([Mousewheel, Navigation, Pagination]);

export default {
  name: "StudentStats",
  components: {
    MobEmptyState,
    Icon,
    LevelStats,
    StudentStatCard,
    PointsEvolutionGraph,
    Swiper,
    SwiperSlide,
  },
  data() {
    return {
      categoriesStats: [],
      statsChunked: [],
      knowledgeAreasStats: [],
      levelStats: {},
      enrolledModules: [],
      olympicRegistered: false,
      olympicModuleId: null,
      olympicSlug: null,
    };
  },
  methods: {
    async getStats() {
      const loader = this.$loading.show({});
      try {
        const data = await this.$store.dispatch("dashboardCache/fetchDashboardDataIfNeeded");
        const stats = data?.stats && typeof data.stats === "object" ? data.stats : {};
        const generalStats = stats.generalStats && typeof stats.generalStats === "object"
          ? stats.generalStats
          : {};
        this.enrolledModules = Array.isArray(data?.enrolledModules) ? data.enrolledModules : [];
        await this.checkOlympicRegistration();
        this.levelStats = generalStats;
        this.$store.dispatch("levelStats/setLevelStats", generalStats);
        const safeStats = { ...stats };
        delete safeStats.generalStats;
        this.categoriesStats = Object.keys(safeStats).map((key) => ({
          category: key,
          stats: safeStats[key],
        }));
        this.statsChunked = this.chunkStats(this.categoriesStats, 2);
      } catch (err) {
        console.error("Error loading stats:", err);
      } finally {
        loader.hide();
      }
    },
    async checkOlympicRegistration() {
      try {
        const { data } = await getEnrolledOlympics();
        const list = Array.isArray(data) ? data : [];
        this.olympicRegistered = list.length > 0;
        const primary = list.find((o) => o.slug === "olimpiadas-mobius-2026") || list[0];
        this.olympicModuleId = primary?.moduleId ?? null;
        this.olympicSlug = primary?.slug ?? null;
      } catch (_) {
        this.olympicRegistered = false;
        this.olympicModuleId = null;
        this.olympicSlug = null;
      }
    },
    chunkStats(arr, size) {
      const chunked_arr = [];
      let index = 0;
      while (index < arr.length) {
        chunked_arr.push(arr.slice(index, size + index));
        index += size;
      }
      return chunked_arr;
    },
  },
  computed: {
    generalStats() {
      return this.levelStats || {};
    },
    totalExercises() {
      return this.generalStats.total || 0;
    },
    totalQuestions() {
      const total = this.generalStats.total || 0;
      return total;
    },
    successRate() {
      if (this.generalStats.successPercentage != null && this.generalStats.successPercentage !== undefined) {
        return this.generalStats.successPercentage;
      }
      const total = this.generalStats.total || 0;
      if (!total) return 0;
      return Math.round(((this.generalStats.correctas || 0) / total) * 100);
    },
    difficultyCounts() {
      return {
        easy: this.generalStats.easyCount || 0,
        medium: this.generalStats.mediumCount || 0,
        hard: this.generalStats.hardCount || 0,
      };
    },
    isRegisteredInOlimpiadas() {
      return this.olympicRegistered;
    },
    olympicCoursePath() {
      if (!this.olympicModuleId) return "/olimpiadas";
      const query = this.olympicSlug ? { slug: this.olympicSlug } : {};
      return { path: `/course/${this.olympicModuleId}/phases`, query };
    },
  },
  mounted() {
    this.getStats();
  },
  activated() {
    this.getStats();
  },
};
</script>

<style>
.stats-cards .swiper-container {
  height: 170px;
  width: 100%;
}

.stats-cards .swiper-slide {
  width: 296px;
}

.stats-cards .swiper-container-horizontal > .swiper-pagination-bullets {
  bottom: -7px;
}

.stats-cards .swiper-button-prev,
.stats-cards .swiper-button-next {
  top: 45%;
}

.stats-cards .swiper-button-prev:after,
.stats-cards .swiper-button-next:after {
  font-size: 30px;
}
</style>
